import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI, s_PL } from '../../shared/styles';
import { useStorage } from '../../shared/hooks';
import { CLAUDE_HEADERS } from '../../shared/api';
import RingGauge from '../../components/ui/RingGauge';
import RadarChart from '../../components/ui/RadarChart';
import LevelUpModal from '../../modals/LevelUpModal';
import { GROWTH_CATEGORIES } from './constants';
import { playSound } from '../../shared/soundEngine';
import { EXERCISE_GROWTH_MAP } from '../exercises/constants';

// Level-up check using weighted XP
function checkLevelUp(catId, currentLevel, exHistory) {
  if (currentLevel >= 5) return false;
  const cat = GROWTH_CATEGORIES.find(c=>c.id===catId);
  if (!cat) return false;
  const levelDef = cat.levels[currentLevel-1];
  const windowMs = 30 * 24 * 3600 * 1000;
  const recent = exHistory.filter(h => h.catId===catId && h.level===currentLevel && h.date > Date.now()-windowMs);
  // Count sessions weighted by XP (xp=3 counts as 1.5 sessions, xp=1 as 0.5)
  const weightedSessions = recent.reduce((a,h) => a + (h.xp===3?1.5:h.xp===2?1:0.5), 0);
  if (weightedSessions < levelDef.sessions) return false;
  const days = new Set(recent.map(h=>new Date(h.date).toDateString())).size;
  if (days < levelDef.days) return false;
  const withFeelings = recent.filter(h=>h.feelings&&h.feelings.length>0);
  if (withFeelings.length < Math.ceil(recent.length*0.4)) return false;
  return true;
}

function getProgressToNextLevel(catId, currentLevel, exHistory) {
  if (currentLevel >= 5) return {pct:100, sessionsLeft:0, daysLeft:0, sessions:0, sessionsNeeded:0, daysSpread:0, daysNeeded:0};
  const cat = GROWTH_CATEGORIES.find(c=>c.id===catId);
  if (!cat) return {pct:0, sessionsLeft:0, daysLeft:0};
  const levelDef = cat.levels[currentLevel-1];
  const windowMs = 30 * 24 * 3600 * 1000;
  const recent = exHistory.filter(h=>h.catId===catId&&h.level===currentLevel&&h.date>Date.now()-windowMs);
  const weightedSessions = recent.reduce((a,h)=>a+(h.xp===3?1.5:h.xp===2?1:0.5),0);
  const days = new Set(recent.map(h=>new Date(h.date).toDateString())).size;
  const sessionPct = Math.min(1, weightedSessions / levelDef.sessions);
  const dayPct = Math.min(1, days / levelDef.days);
  const pct = Math.round(((sessionPct + dayPct) / 2) * 100);
  return {
    pct, sessions: Math.round(weightedSessions), sessionsNeeded: levelDef.sessions,
    daysSpread: days, daysNeeded: levelDef.days,
    sessionsLeft: Math.max(0, Math.ceil(levelDef.sessions - weightedSessions)),
    daysLeft: Math.max(0, levelDef.days - days),
  };
}

function GoalsScreen({lang}) {
  const nl = lang==="nl";
  const [tab, setTab] = useState("matrix");
  const [catLevels, setCatLevels] = useStorage("sl_cat_levels", {});
  const [exHistory] = useStorage("sl_ex_history", []);
  const [weekGoals, setWeekGoals] = useStorage("sl_week_goals", []);
  const [selCat, setSelCat] = useState(null);
  const [levelUp, setLevelUp] = useState(null);
  const [goals, setGoals] = useStorage("sl_goals", []);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [ng, setNG] = useState({title:"",category:"focus",target:7,current:0});
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  // Ensure all cats have at least level 1
  const levels = {};
  GROWTH_CATEGORIES.forEach(c=>{ levels[c.id]=catLevels[c.id]||1; });

  // Check for level-ups on load
  useEffect(()=>{
    GROWTH_CATEGORIES.forEach(cat=>{
      const cur=levels[cat.id];
      if(checkLevelUp(cat.id, cur, exHistory)&&cur<5){
        const newLv=cur+1;
        setCatLevels(prev=>({...prev,[cat.id]:newLv}));
        setLevelUp({category:cat,newLevel:newLv});
      }
    });
  },[exHistory]);

  // AI coach suggestion for weakest category
  const getAiSuggestion = async()=>{
    setLoadingAi(true);
    const weakest = GROWTH_CATEGORIES.slice().sort((a,b)=>(levels[a.id]||1)-(levels[b.id]||1)).slice(0,2);
    try{
      const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:CLAUDE_HEADERS,body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:120,system:"Warme wellness coach. Max 40 woorden. Geef een persoonlijk weekadvies.",messages:[{role:"user",content:`Mijn zwakste categorieën zijn: ${weakest.map(c=>nl?c.label:c.labelEN).join(" en ")} (beide level ${levels[weakest[0].id]}). Geef een motiverend weekadvies in het ${nl?"Nederlands":"Engels"}.`}]})});
      const d=await res.json();setAiSuggestion(d.content?.[0]?.text||null);
    }catch{setAiSuggestion(null);}
    setLoadingAi(false);
  };

  const toggleWeekGoal=(catId)=>{
    setWeekGoals(prev=>prev.includes(catId)?prev.filter(x=>x!==catId):[...prev,catId].slice(0,3));
  };

  const TABS=[
    {id:"matrix",icon:"🕸️",label:nl?"Groeimatrix":"Growth Matrix"},
    {id:"weekdoel",icon:"🎯",label:nl?"Weekdoel":"Week Goal"},
    {id:"doelen",icon:"📋",label:nl?"Doelen":"Goals"},
  ];

  const catGoalOpts=[
    {id:"focus",icon:"🎯",label:nl?"Mentale Focus":"Mental Focus",c:"#D4873A"},
    {id:"body",icon:"💪",label:nl?"Lichaamsbewustzijn":"Body Awareness",c:"#C06090"},
    {id:"emotion",icon:"❤️",label:nl?"Emotieregulatie":"Emotion Reg.",c:"#C05050"},
    {id:"rest",icon:"🌿",label:nl?"Rust & Herstel":"Rest & Recovery",c:"#2A9E82"},
  ];

  return (
    <div className="fadeUp">
      {levelUp&&<LevelUpModal lang={lang} category={levelUp.category} newLevel={levelUp.newLevel} onClose={()=>setLevelUp(null)}/>}

      {/* Tab bar */}
      <div style={{display:"flex",background:"rgba(22,37,64,0.65)",borderRadius:14,padding:4,marginBottom:16,gap:2}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>{playSound('tab');setTab(t.id);}} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,padding:"9px 4px",borderRadius:10,border:"none",cursor:"pointer",background:tab===t.id?`linear-gradient(135deg,${_T.grad1},${_T.grad2})`:"transparent"}}>
            <span style={{fontSize:16,lineHeight:1}}>{t.icon}</span>
            <span style={{fontSize:9,fontWeight:tab===t.id?700:400,color:tab===t.id?"#0a1220":C.silver,whiteSpace:"nowrap",fontFamily:"Inter,sans-serif"}}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* ── GROWTH MATRIX TAB ── */}
      {tab==="matrix"&&(
        <div>
          {/* Radar chart */}
          <div style={s_GC({textAlign:"center",padding:"20px 10px",marginBottom:14})}>
            <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>{nl?"JOUW GROEIMATRIX":"YOUR GROWTH MATRIX"}</div>
            <RadarChart levels={levels}/>
            <div style={{display:"flex",flexWrap:"wrap",justifyContent:"center",gap:8,marginTop:12}}>
              {GROWTH_CATEGORIES.map(c=>(
                <div key={c.id} style={{display:"flex",alignItems:"center",gap:4}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:c.color}}/>
                  <span style={{fontSize:9,color:C.silver}}>{nl?c.label.split(" ")[0]:c.labelEN.split(" ")[0]} Lv{levels[c.id]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Category cards — tap for detail */}
          {selCat ? (
            <div className="slideIn">
              <button onClick={()=>setSelCat(null)} style={{background:"none",border:"none",color:C.silver,cursor:"pointer",fontSize:13,marginBottom:12,display:"flex",alignItems:"center",gap:4}}>← {nl?"Terug":"Back"}</button>
              {(()=>{
                const cat=GROWTH_CATEGORIES.find(c=>c.id===selCat);
                const curLv=levels[selCat];
                const prog=getProgressToNextLevel(selCat,curLv,exHistory);
                return (
                  <div>
                    <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:14}}>
                      <div style={{width:52,height:52,borderRadius:16,background:`${cat.color}20`,border:`2px solid ${cat.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>{cat.icon}</div>
                      <div>
                        <div style={{color:C.white,fontSize:18,fontWeight:700,fontFamily:"Georgia,serif"}}>{nl?cat.label:cat.labelEN}</div>
                        <div style={{color:cat.color,fontSize:13,fontWeight:600}}>Level {curLv} / 5</div>
                      </div>
                    </div>
                    {/* Progress to next */}
                    {curLv<5&&(
                      <div style={s_GC({marginBottom:14})}>
                        <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:8}}>{nl?"VOORTGANG NAAR LEVEL":"PROGRESS TO LEVEL"} {curLv+1}</div>
                        <div style={{height:8,background:_T.bg4,borderRadius:4,overflow:"hidden",marginBottom:8}}>
                          <div style={{width:`${prog.pct}%`,height:"100%",background:`linear-gradient(90deg,${cat.color}88,${cat.color})`,borderRadius:4,transition:"width 0.5s"}}/>
                        </div>
                        <div style={{display:"flex",gap:12}}>
                          <div style={{fontSize:11,color:C.silver}}>📅 {prog.sessions}/{prog.sessionsNeeded} {nl?"sessies":"sessions"}</div>
                          <div style={{fontSize:11,color:C.silver}}>🗓️ {prog.daysSpread}/{prog.daysNeeded} {nl?"dagen":"days"}</div>
                          <div style={{fontSize:11,color:cat.color,fontWeight:600,marginLeft:"auto"}}>{prog.pct}%</div>
                        </div>
                      </div>
                    )}
                    {curLv>=5&&<div style={{...s_GC({textAlign:"center",padding:16}),color:C.teal,fontSize:13,fontWeight:700}}>🏆 {nl?"Max level bereikt!":"Max level reached!"}</div>}
                    {/* All levels */}
                    <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:10}}>{nl?"ALLE NIVEAUS":"ALL LEVELS"}</div>
                    {cat.levels.map(lv=>{
                      const isDone=curLv>lv.n;const isCur=curLv===lv.n;const isLocked=curLv<lv.n;
                      return (
                        <div key={lv.n} style={{...s_GC({padding:14,marginBottom:8}),borderTop:`1.5px solid ${isDone?C.teal:isCur?cat.color:_T.cardBorderTop}`,opacity:isLocked?0.45:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                            <div style={{width:28,height:28,borderRadius:"50%",background:isDone?`${C.teal}20`:isCur?`${cat.color}20`:_T.bg4,border:`1.5px solid ${isDone?C.teal:isCur?cat.color:_T.cardBorderBot}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:isDone?C.teal:isCur?cat.color:C.silver}}>
                              {isDone?"✓":lv.n}
                            </div>
                            <div style={{flex:1}}>
                              <div style={{color:isLocked?C.silver:C.white,fontSize:13,fontWeight:600}}>{nl?lv.goal:lv.goalEN}</div>
                              <div style={{color:C.silver,fontSize:11}}>{nl?lv.exercise:lv.exerciseEN}</div>
                            </div>
                            <div style={{fontSize:13,color:C.silver,background:_T.bg4,padding:"2px 8px",borderRadius:20}}>{lv.duration}</div>
                          </div>
                          {isCur&&<div style={{fontSize:13,color:cat.color,fontWeight:600,marginTop:4}}>← {nl?"Jouw huidige level":"Your current level"}</div>}
                          {isLocked&&<div style={{fontSize:13,color:C.silver,marginTop:4}}>🔒 {nl?"Voltooi level":"Complete level"} {lv.n-1} {nl?"om te ontgrendelen":"to unlock"} · {lv.sessions} {nl?"sessies nodig":"sessions needed"}, {lv.days} {nl?"dagen":"days"}</div>}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ):(
            <div>
              <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:10}}>{nl?"CATEGORIEËN":"CATEGORIES"}</div>
              {GROWTH_CATEGORIES.map(cat=>{
                const curLv=levels[cat.id];
                const prog=getProgressToNextLevel(cat.id,curLv,exHistory);
                return (
                  <div key={cat.id} onClick={()=>setSelCat(cat.id)} style={{...s_GC({cursor:"pointer",padding:14,marginBottom:8})}}>
                    <div style={{display:"flex",gap:12,alignItems:"center"}}>
                      <div style={{width:44,height:44,borderRadius:13,background:`${cat.color}18`,border:`1.5px solid ${cat.color}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{cat.icon}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                          <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?cat.label:cat.labelEN}</div>
                          <div style={{color:cat.color,fontSize:12,fontWeight:700}}>Lv {curLv}</div>
                        </div>
                        <div style={{height:4,background:_T.bg4,borderRadius:2,overflow:"hidden",marginBottom:4}}>
                          <div style={{width:`${curLv>=5?100:prog.pct}%`,height:"100%",background:`linear-gradient(90deg,${cat.color}66,${cat.color})`,borderRadius:2}}/>
                        </div>
                        <div style={{fontSize:13,color:C.silver}}>{curLv>=5?(nl?"Max level! 🏆":"Max level! 🏆"):`${prog.sessionsLeft} ${nl?"sessies":"sessions"} · ${prog.daysLeft} ${nl?"dagen voor":"days for"} Lv ${curLv+1}`}</div>
                      </div>
                      <div style={{color:C.silver,fontSize:18}}>›</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── WEEK GOAL TAB ── */}
      {tab==="weekdoel"&&(
        <div>
          {/* AI suggestion */}
          <div style={s_GC({marginBottom:14})}>
            <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:8}}>🦋 SENSE LIFTING COACH</div>
            {aiSuggestion?(
              <div style={{color:C.silver2,fontSize:13,lineHeight:1.7,fontStyle:"italic",marginBottom:10}}>"{aiSuggestion}"</div>
            ):(
              <div style={{color:C.silver,fontSize:12,marginBottom:10}}>{nl?"Laat de Sense Lifting Coach je weekadvies geven op basis van je zwakste categorieën.":"Let the Sense Lifting Coach give you weekly advice based on your weakest categories."}</div>
            )}
            <button style={s_GB({width:"100%",fontSize:12,opacity:loadingAi?0.5:1})} onClick={getAiSuggestion} disabled={loadingAi}>
              {loadingAi?"🦋 ···":(nl?"🦋 Vraag weekadvies":"🦋 Get week advice")}
            </button>
          </div>
          {/* Category picker */}
          <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:8}}>{nl?"KIES FOCUSCATEGORIEËN DEZE WEEK (max 3)":"CHOOSE FOCUS CATEGORIES THIS WEEK (max 3)"}</div>
          {GROWTH_CATEGORIES.map(cat=>{
            const curLv=levels[cat.id];const isSelected=weekGoals.includes(cat.id);
            return (
              <div key={cat.id} onClick={()=>toggleWeekGoal(cat.id)} style={{...s_GC({cursor:"pointer",padding:14,marginBottom:8,borderTop:`1.5px solid ${isSelected?cat.color:_T.cardBorderTop}`}),background:isSelected?`${cat.color}10`:_T.bg3}}>
                <div style={{display:"flex",gap:12,alignItems:"center"}}>
                  <div style={{width:38,height:38,borderRadius:11,background:`${cat.color}18`,border:`1.5px solid ${isSelected?cat.color:cat.color+"33"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{cat.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{color:isSelected?cat.color:C.white,fontSize:13,fontWeight:600}}>{nl?cat.label:cat.labelEN}</div>
                    <div style={{color:C.silver,fontSize:11}}>Lv {curLv} · {nl?cat.levels[curLv-1].goal:cat.levels[curLv-1].goalEN}</div>
                  </div>
                  <div style={{width:22,height:22,borderRadius:"50%",background:isSelected?`${cat.color}30`:_T.bg4,border:`1.5px solid ${isSelected?cat.color:_T.cardBorderBot}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:isSelected?cat.color:C.silver}}>
                    {isSelected?"✓":"○"}
                  </div>
                </div>
              </div>
            );
          })}
          {weekGoals.length>0&&(
            <div style={s_GC({padding:"12px 16px",marginTop:4})}>
              <div style={{fontSize:13,color:C.teal,letterSpacing:3,fontWeight:600,marginBottom:8}}>✓ {nl?"JOUW WEEKFOCUS":"YOUR WEEK FOCUS"}</div>
              {weekGoals.map(id=>{const cat=GROWTH_CATEGORIES.find(c=>c.id===id);const lv=cat.levels[levels[id]-1];return(
                <div key={id} style={{display:"flex",gap:8,alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${_T.cardBorderBot}`}}>
                  <span style={{fontSize:16}}>{cat.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{color:C.white,fontSize:12,fontWeight:600}}>{nl?cat.label:cat.labelEN}</div>
                    <div style={{color:C.silver,fontSize:11}}>{nl?lv.exercise:lv.exerciseEN} · {lv.duration}</div>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>
      )}

      {/* ── GOALS TAB ── */}
      {tab==="doelen"&&(
        <div>
          {/* Explanation section */}
          <div style={s_GC({marginBottom:14})}>
            <div style={{fontSize:13,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:12}}>💡 {nl?"HOE WERKT HET SYSTEEM":"HOW THE SYSTEM WORKS"}</div>

            {/* Level system */}
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <div style={{width:28,height:28,borderRadius:8,background:`${_T.accent}20`,border:`1px solid ${_T.accent}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>🕸️</div>
                <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?"7 Groeicategorieën":"7 Growth Categories"}</div>
              </div>
              <div style={{color:C.silver,fontSize:12,lineHeight:1.7,paddingLeft:36}}>
                {nl
                  ?"Elke oefening traint 2–3 van de 7 categorieën tegelijk. Per categorie groei je van level 1 naar level 5 door consistent te oefenen."
                  :"Every exercise trains 2–3 of the 7 categories simultaneously. Per category you grow from level 1 to level 5 through consistent practice."}
              </div>
            </div>

            {/* XP weights */}
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{width:28,height:28,borderRadius:8,background:`${C.teal}20`,border:`1px solid ${C.teal}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>⚡</div>
                <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?"Groei-effect per oefening":"Growth effect per exercise"}</div>
              </div>
              <div style={{paddingLeft:36,display:"flex",flexDirection:"column",gap:6}}>
                {[
                  {xp:3,label:nl?"Groot effect":"Large effect",weight:"1.5×",color:C.teal},
                  {xp:2,label:nl?"Matig effect":"Medium effect",weight:"1.0×",color:_T.accent},
                  {xp:1,label:nl?"Klein effect":"Small effect",weight:"0.5×",color:C.silver},
                ].map(row=>(
                  <div key={row.xp} style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:48,height:5,background:_T.bg4,borderRadius:3,overflow:"hidden",flexShrink:0}}>
                      <div style={{width:`${(row.xp/3)*100}%`,height:"100%",background:row.color,borderRadius:3}}/>
                    </div>
                    <span style={{fontSize:11,color:row.color,fontWeight:600,minWidth:36}}>{row.weight}</span>
                    <span style={{fontSize:11,color:C.silver}}>{row.label}</span>
                  </div>
                ))}
                <div style={{color:C.silver,fontSize:11,marginTop:4,lineHeight:1.5}}>
                  {nl
                    ?"Een oefening met groot effect telt 1,5× mee voor je level-up voortgang in die categorie."
                    :"An exercise with large effect counts 1.5× toward your level-up progress in that category."}
                </div>
              </div>
            </div>

            {/* Level-up requirements */}
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{width:28,height:28,borderRadius:8,background:`${C.purple}20`,border:`1px solid ${C.purple}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>🏅</div>
                <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?"Level-up voorwaarden":"Level-up requirements"}</div>
              </div>
              <div style={{paddingLeft:36}}>
                <div style={{display:"grid",gridTemplateColumns:"auto 1fr 1fr",gap:"4px 12px",marginBottom:6}}>
                  <div style={{fontSize:13,color:C.silver,fontWeight:700}}>Level</div>
                  <div style={{fontSize:13,color:C.silver,fontWeight:700}}>{nl?"Sessies":"Sessions"}</div>
                  <div style={{fontSize:13,color:C.silver,fontWeight:700}}>{nl?"Dagen":"Days"}</div>
                  {[
                    {from:1,to:2,sessions:5,days:3},
                    {from:2,to:3,sessions:7,days:4},
                    {from:3,to:4,sessions:10,days:5},
                    {from:4,to:5,sessions:12,days:7},
                  ].map(r=>(
                    <>
                      <div key={r.from+"l"} style={{fontSize:11,color:_T.accent,fontWeight:700}}>{r.from}→{r.to}</div>
                      <div key={r.from+"s"} style={{fontSize:11,color:C.silver2}}>{r.sessions}×</div>
                      <div key={r.from+"d"} style={{fontSize:11,color:C.silver2}}>{r.days} {nl?"dagen":"days"}</div>
                    </>
                  ))}
                </div>
                <div style={{color:C.silver,fontSize:11,lineHeight:1.5}}>
                  {nl
                    ?"+ minimaal 40% van sessies moet een gevoel-reflectie bevatten."
                    :"+ at least 40% of sessions must include a feeling reflection."}
                </div>
              </div>
            </div>

            {/* Exercise → category map */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{width:28,height:28,borderRadius:8,background:`${C.orange}20`,border:`1px solid ${C.orange}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>🧘</div>
                <div style={{color:C.white,fontSize:13,fontWeight:600}}>{nl?"Oefening → Groei-effect":"Exercise → Growth effect"}</div>
              </div>
              <div style={{paddingLeft:36,display:"flex",flexDirection:"column",gap:8}}>
                {Object.entries(EXERCISE_GROWTH_MAP).map(([exId,contribs])=>{
                  const exLabels = {
                    breathing:{nl:"Ademhaling",en:"Breathing"},
                    meditation:{nl:"Meditatie",en:"Meditation"},
                    bodyscan:{nl:"Bodyscan",en:"Body Scan"},
                    focus:{nl:"Focus Timer",en:"Focus Timer"},
                    walking:{nl:"Wandeling",en:"Walking"},
                    stretching:{nl:"Stretching",en:"Stretching"},
                    gratitude:{nl:"Dankbaarheid",en:"Gratitude"},
                    yoga:{nl:"Yoga Flow",en:"Yoga Flow"},
                    powerpose:{nl:"Powerpose",en:"Power Pose"},
                  };
                  const sorted=Object.entries(contribs).sort((a,b)=>b[1]-a[1]);
                  return (
                    <div key={exId}>
                      <div style={{color:C.silver2,fontSize:11,fontWeight:600,marginBottom:4}}>
                        {nl?exLabels[exId]?.nl:exLabels[exId]?.en}
                      </div>
                      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                        {sorted.map(([catId,xp])=>{
                          const cat=GROWTH_CATEGORIES.find(c=>c.id===catId);
                          if(!cat)return null;
                          return (
                            <div key={catId} style={{display:"flex",alignItems:"center",gap:3,background:`${cat.color}15`,border:`1px solid ${cat.color}33`,borderRadius:20,padding:"2px 8px"}}>
                              <span style={{fontSize:13}}>{cat.icon}</span>
                              <span style={{fontSize:9,color:cat.color,fontWeight:700}}>{"●".repeat(xp)+"○".repeat(3-xp)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Custom goals */}
          <button onClick={()=>setShowAddGoal(!showAddGoal)} style={s_PB({width:"100%",marginBottom:14})}>+ {nl?"Nieuw doel":"New goal"}</button>
          {showAddGoal&&(
            <div style={s_GC({marginBottom:14})}>
              <input value={ng.title} onChange={e=>setNG({...ng,title:e.target.value})} placeholder={nl?"Omschrijf je doel...":"Describe your goal..."} style={s_TI({marginBottom:12})}/>
              <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:12}}>
                {catGoalOpts.map(c=><button key={c.id} style={s_PL(ng.category===c.id,c.c)} onClick={()=>setNG({...ng,category:c.id})}>{c.icon} {c.label}</button>)}
              </div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,color:C.silver,marginBottom:4}}>Target: <span style={{color:_T.accent,fontWeight:700}}>{ng.target}x</span></div>
                <input type="range" min={1} max={30} value={ng.target} onChange={e=>setNG({...ng,target:+e.target.value})} style={{width:"100%",accentColor:_T.accent}}/>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button style={s_PB()} onClick={()=>{if(!ng.title.trim())return;setGoals([...goals,{...ng,id:Date.now()}]);setNG({title:"",category:"focus",target:7,current:0});setShowAddGoal(false);}}>Save</button>
                <button style={s_GB()} onClick={()=>setShowAddGoal(false)}>✕</button>
              </div>
            </div>
          )}
          {goals.length===0&&!showAddGoal&&<div style={{...s_GC({textAlign:"center",padding:32}),fontSize:14,color:C.silver}}>{nl?"Voeg je eerste doel toe!":"Add your first goal!"}</div>}
          {goals.map(g=>{
            const cat=catGoalOpts.find(c=>c.id===g.category)||catGoalOpts[0];
            const pct=Math.round((g.current/g.target)*100);
            return(
              <div key={g.id} style={s_GC()}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{width:36,height:36,borderRadius:10,background:`${cat.c}15`,border:`1px solid ${cat.c}25`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{cat.icon}</div>
                    <div>
                      <div style={{color:cat.c,fontSize:9,fontWeight:600,letterSpacing:2}}>{cat.label.toUpperCase()}</div>
                      <div style={{color:C.white,fontSize:13,fontWeight:600}}>{g.title}</div>
                    </div>
                  </div>
                  <button onClick={()=>setGoals(goals.filter(x=>x.id!==g.id))} style={{background:"none",border:"none",color:`${C.red}66`,cursor:"pointer",fontSize:16}}>✕</button>
                </div>
                <div style={{height:6,background:_T.bg4,borderRadius:3,overflow:"hidden",marginBottom:10}}>
                  <div style={{width:`${pct}%`,height:"100%",background:`linear-gradient(90deg,${cat.c}66,${cat.c})`,borderRadius:3}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  {g.current<g.target
                    ?<button onClick={()=>setGoals(goals.map(x=>x.id===g.id?{...x,current:Math.min(x.current+1,x.target)}:x))} style={{background:`${cat.c}15`,border:`1px solid ${cat.c}33`,borderRadius:10,padding:"6px 14px",color:cat.c,fontSize:12,cursor:"pointer",fontWeight:600}}>+ Session</button>
                    :<div style={{color:C.teal,fontSize:12,fontWeight:700}}>🎉 {nl?"Behaald!":"Achieved!"}</div>
                  }
                  <div style={{fontSize:12,color:C.silver}}>{g.current}/{g.target}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GoalsScreen;
